import { useCallback, useMemo } from 'react';

import {
  AMICI_FINTI,
  MODALITA_TEMPO_FINTE,
} from '@/lib/myBoards/datiFinti';
import { sipuoChiedereLaStima, statoDellaStima } from '@/lib/myBoards/analisi';
import { erroreBoard, type ErroreBoard } from '@/lib/myBoards/errori';
import { statsDi } from '@/lib/myBoards/mosse';
import { sorgenteDelFiltro, type FiltroBoard } from '@/lib/myBoards/riga';
import {
  useDeleteBoardMutation,
  useGetBoardsInfiniteQuery,
  useGetMeQuery,
  useGetPositionAnalysisQuery,
  useGetPositionQuery,
} from '@/services/qgammonApi';
import type {
  AnalysisState,
  BoardCommunity,
  BoardView,
  ModalitaTempoSfida,
  OriginView,
  PositionStatsView,
  PositionView,
  ScenarioView,
} from '@/types/myBoards';

/**
 * 🔴 IL PUNTO UNICO in cui «My Boards» prende i dati. Dal T1 sono VERI: la lista da `GET /boards` (api, a cursore, il filtro
 * e' lato server), la scheda da `GET /positions/:id` (api, pubblica) e dalla stima `GET /positions/:id/analysis` (gameserver),
 * il «Togli» da `DELETE /boards/:id`, l'aritmetica da `positionStats` (`qgammon-rules`, nel client). I componenti non importano
 * ne' `qgammonApi` ne' `datiFinti`: se lo facessero, un cambio di contratto andrebbe cercato uno per uno.
 * ⛔ Eccezione dichiarata: il foglio «Sfida un amico da qui» (`useSfidaDaBoard`) resta T0, con amici e tempi FINTI — e' il T2.
 */

export type StatoLista = 'caricamento' | 'errore' | 'pronto';

/**
 * Cio' che la lista sa: i board del giocatore (privati, il piu' recente in cima, per il filtro scelto), chi e' lui (per scrivere
 * «Tu») e `statsDi`, l'aritmetica di una posizione (il pip della riga): `positionStats` calcolata nel client sulla posizione,
 * una funzione sincrona, non una chiamata per riga.
 * La lista e' a CURSORE: nessun totale (il «Tutti» e' senza numero), «Mostra altre» porta la pagina dopo.
 */
export function useMyBoards(filtro: FiltroBoard = 'all'): {
  boards: BoardView[];
  mioId: string | null;
  statsDi: (position: PositionView) => PositionStatsView;
  stato: StatoLista;
  /** Prima pagina fallita: `riprova` la rilegge. */
  riprova: () => void;
  haAltre: boolean;
  staCaricandoAltre: boolean;
  /** La pagina dopo e' fallita (la lista che c'e' resta); `caricaAltre` la ritenta. */
  erroreAltre: boolean;
  caricaAltre: () => void;
} {
  const q = useGetBoardsInfiniteQuery(sorgenteDelFiltro(filtro) ?? 'all');
  const me = useGetMeQuery();
  const boards = useMemo(() => {
    // Un board puo' ritrovarsi in due pagine se ne e' arrivato uno nuovo fra una lettura e l'altra: una riga per id.
    const visti = new Set<string>();
    const tutti: BoardView[] = [];
    for (const pagina of q.data?.pages ?? []) {
      for (const b of pagina.items) {
        if (visti.has(b.id)) continue;
        visti.add(b.id);
        tutti.push(b);
      }
    }
    return tutti;
  }, [q.data]);
  const { refetch, fetchNextPage } = q;
  return {
    boards,
    mioId: me.data?.user.id ?? null,
    statsDi,
    stato: q.data ? 'pronto' : q.isError ? 'errore' : 'caricamento',
    riprova: useCallback(() => void refetch(), [refetch]),
    haAltre: q.hasNextPage,
    staCaricandoAltre: q.isFetchingNextPage,
    erroreAltre: q.isError && q.data !== undefined,
    caricaAltre: useCallback(() => void fetchNextPage(), [fetchNextPage]),
  };
}

/**
 * Il contorno di un board per la scheda: la partita d'origine com'e' ADESSO, la comunita', lo scenario e le stime, dalla
 * risposta di `GET /positions/:id` (la posizione, congelata, la scheda ce l'ha gia' dalla lista). L'aritmetica e' di
 * `positionStats` e non aspetta nessuna rete.
 *  - `caricamento`: la risposta non e' ancora arrivata;
 *  - `non-trovato`: 404 (la posizione non c'e' piu' o non si puo' piu' vedere);
 *  - `errore`: rete o altro, `riprova` la rilegge;
 *  - `pronto`: `origin`, `community`, `scenario` (`null` = non giocabile) e `analysis` sono veri.
 */
export interface BoardDettaglio {
  stato: 'caricamento' | 'non-trovato' | 'errore' | 'pronto';
  stats: PositionStatsView;
  /** Quella della risposta quando c'e' (e' la partita d'origine ADESSO), altrimenti quella che la lista aveva. */
  origin: OriginView;
  community: BoardCommunity | null;
  /** `null` finche' non si sa, e `null` per una posizione non giocabile: `sfidabile` guarda anche `stato`. */
  scenario: ScenarioView | null;
  analysis: AnalysisState;
  riprova: () => void;
  riprovaAnalisi: () => void;
}

export function useBoardDettaglio(board: BoardView): BoardDettaglio {
  const id = board.position.id;
  // Aperta la scheda si rilegge SEMPRE: l'origine puo' essere finita, la comunita' cresce.
  const pos = useGetPositionQuery(id, { refetchOnMountOrArgChange: true });
  const origine = pos.data?.origin ?? null;
  const stima = useGetPositionAnalysisQuery(id, {
    skip: !sipuoChiedereLaStima(board.position.phase, origine),
  });
  const stats = useMemo(() => statsDi(board.position), [board.position]);
  const { refetch: rileggi } = pos;
  const { refetch: rileggiStima } = stima;
  // `404` e `400 invalid_position_id` dicono la stessa cosa a chi guarda: la posizione non c'e' (piu').
  const codice = pos.isError ? erroreBoard(pos.error).codice : null;
  const stato = pos.data
    ? 'pronto'
    : codice === 'not_found' || codice === 'invalid_position_id'
      ? 'non-trovato'
      : pos.isError
        ? 'errore'
        : 'caricamento';
  return {
    stato,
    stats,
    origin: pos.data?.origin ?? board.origin,
    community: pos.data?.community ?? null,
    scenario: pos.data?.scenario ?? null,
    analysis: statoDellaStima({ fase: board.position.phase, origine, analisi: { data: stima.data, error: stima.error } }),
    riprova: () => void rileggi(),
    riprovaAnalisi: () => void rileggiStima(),
  };
}

/**
 * «Togli»: nasconde un board dalla propria lista. Torna `null` se e' tolto — anche per un `404`, perche' «non c'e'» e' cio' che
 * si voleva — oppure l'errore da dire. La lista si rilegge in ogni caso: RTK invalida i tag anche su una mutation RIFIUTATA
 * (`isRejectedWithValue`), quindi un `404` («non e' piu' li'») riallinea la lista senza un'invalidazione nostra
 * (`tests/my-boards-endpoint.test.tsx`: il mutante che la toglie non c'e', perche' non serve).
 */
export function useTogliBoard(): { togli: (id: string) => Promise<ErroreBoard | null>; inCorso: boolean } {
  const [elimina, { isLoading }] = useDeleteBoardMutation();
  const togli = useCallback(
    async (id: string): Promise<ErroreBoard | null> => {
      const r = await elimina(id);
      if (!('error' in r)) return null;
      const e = erroreBoard(r.error);
      return e.codice === 'not_found' ? null : e;
    },
    [elimina],
  );
  return { togli, inCorso: isLoading };
}

/** Un amico da sfidare dal foglio (T6): online in cima, poi per nome. */
export interface AmicoSfida {
  userId: string;
  nome: string;
  online: boolean;
}

/**
 * Gli amici (la stessa fonte della lobby, TMA #481, dal T2) e le modalita' di tempo del server (`match-modes`, 1 punto).
 * 🔴 T0 ANCORA: dati FINTI — la sfida da un board e' il T2 (Q4, G1, P1), fuori da questo cablaggio.
 */
export function useSfidaDaBoard(): { amici: AmicoSfida[]; modalita: ModalitaTempoSfida[] } {
  return useMemo(
    () => ({
      amici: [...AMICI_FINTI].sort((a, b) => Number(b.online) - Number(a.online) || a.nome.localeCompare(b.nome, 'en')),
      modalita: MODALITA_TEMPO_FINTE,
    }),
    [],
  );
}
